import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  TextField,
  Box,
  Chip,
  Stack,
  FormControl,
  FormLabel,
  Divider,
  Typography,
  InputAdornment,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

const EmailInput = ({ label, emails, onChange }) => {
  const [inputValue, setInputValue] = useState("");

  const handleAdd = (e) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      if (
        !emails.includes(inputValue.trim()) &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue.trim())
      ) {
        onChange([...emails, inputValue.trim()]);
        setInputValue("");
      }
    }
  };

  const handleDelete = (emailToDelete) => {
    onChange(emails.filter((email) => email !== emailToDelete));
  };

  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label={`${label}`}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleAdd}
        placeholder="user@example.com (Press Enter)"
        variant="outlined"
        size="small"
      />
      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
        {emails.map((email) => (
          <Chip
            key={email}
            label={email}
            onDelete={() => handleDelete(email)}
          />
        ))}
      </Stack>
    </Box>
  );
};

const ProjectSettingsModal = ({
  open,
  handleClose,
  project,
  handleUpdateProject,
}) => {
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    sendToType: "org",
    toEmails: [],
    ccEmails: [],
  });
  const [enableReportAnalysis, setEnableReportAnalysis] = useState(false);
  const [enableProjectHealthStats, setEnableProjectHealthStats] = useState(false);
  const [stablePercentageInput, setStablePercentageInput] = useState("80");

  useEffect(() => {
    if (project) {
      if (project.emailSettings) {
        const settings = project.emailSettings;
        setEmailSettings({
          enabled: settings.enabled || false,
          sendToType: settings.sendToType || "org",
          toEmails: settings.toEmails || [],
          ccEmails: settings.ccEmails || [],
        });
      }
      setEnableReportAnalysis(project.enableReportAnalysis || false);
      setEnableProjectHealthStats(project.enableProjectHealthStats || false);
      setStablePercentageInput(String(project.stablePercentage ?? 80));
    } else {
      setEmailSettings({
        enabled: false,
        sendToType: "org",
        toEmails: [],
        ccEmails: [],
      });
      setEnableReportAnalysis(false);
      setEnableProjectHealthStats(false);
      setStablePercentageInput("80");
    }
  }, [project, open]);

  const handleSave = async () => {
    const parsed = parseInt(stablePercentageInput, 10);
    const stablePercentage = (!isNaN(parsed) && parsed >= 1 && parsed <= 100) ? parsed : 80;
    console.log('[ProjectSettingsModal] Saving stablePercentage:', stablePercentage, 'project._id:', project?._id);
    await handleUpdateProject({
      ...project,
      emailSettings,
      enableReportAnalysis,
      enableProjectHealthStats,
      stablePercentage,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle variant="h3">Project Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ minWidth: "300px", width: "100%" }}>
          <FormControl fullWidth sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={enableProjectHealthStats}
                  onChange={(e) => setEnableProjectHealthStats(e.target.checked)}
                />
              }
              label="Enable Project Health Stats"
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={enableReportAnalysis}
                  onChange={(e) => setEnableReportAnalysis(e.target.checked)}
                />
              }
              label="Enable Report Analysis"
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
              Stable Run Threshold
            </Typography>
            <TextField
              type="number"
              size="small"
              value={stablePercentageInput}
              onChange={(e) => setStablePercentageInput(e.target.value)}
              inputProps={{ min: 1, max: 100 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText="A run is counted as stable if its pass rate meets or exceeds this value (default: 80%)"
              sx={{ maxWidth: 200 }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={emailSettings.enabled}
                  onChange={(e) =>
                    setEmailSettings({
                      ...emailSettings,
                      enabled: e.target.checked,
                    })
                  }
                />
              }
              label="Email send on iterations addition"
            />
          </FormControl>

          {emailSettings.enabled && (
            <Box sx={{ mt: 2 }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">Send To</FormLabel>
                <RadioGroup
                  row
                  value={emailSettings.sendToType}
                  onChange={(e) =>
                    setEmailSettings({
                      ...emailSettings,
                      sendToType: e.target.value,
                    })
                  }>
                  <FormControlLabel
                    value="org"
                    control={<Radio />}
                    label="Org Users"
                  />
                  <FormControlLabel
                    value="specific"
                    control={<Radio />}
                    label="Specific Users"
                  />
                  <FormControlLabel
                    value="both"
                    control={<Radio />}
                    label="Both"
                  />
                </RadioGroup>
              </FormControl>

              {(emailSettings.sendToType === "specific" ||
                emailSettings.sendToType === "both") && (
                  <Box sx={{ mt: 2 }}>
                    <EmailInput
                      label="TO"
                      emails={emailSettings.toEmails}
                      onChange={(newEmails) =>
                        setEmailSettings({
                          ...emailSettings,
                          toEmails: newEmails,
                        })
                      }
                    />
                    <EmailInput
                      label="CC"
                      emails={emailSettings.ccEmails}
                      onChange={(newEmails) =>
                        setEmailSettings({
                          ...emailSettings,
                          ccEmails: newEmails,
                        })
                      }
                    />
                  </Box>
                )}
            </Box>
          )}
        </Box>
        <Divider sx={{ mt: 2 }} />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}>
          Save
        </Button>
        <Button onClick={handleClose} variant="contained" color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectSettingsModal;
